// Figma link chips — the pure half. State → tone, copy keys, offered act.
//
// Split out of the component for the reason `BreakdownCard.tsx:62-90` states
// about its own error map: the server sends a machine CODE, never a sentence,
// and this surface is localized. Rendering a server string would ship English
// into a German session; switching on the code and looking the sentence up in
// the catalog is what keeps both locales real.
//
// Two rules this file exists to keep enforceable:
//
//  1. EVERY member of `FigmaLinkState` gets its OWN sentence. `FIGMA_STATE_COPY`
//     is a `Record<FigmaLinkState, …>`, so a thirteenth state added in
//     `@loom/shared` is a type error here rather than a chip that silently
//     falls through to a generic line. There is no "Figma error." to fall
//     through to — writing one would make the compiler's demand satisfiable
//     by the least useful answer.
//  2. `"unknown"` is the ONE generic, and it is not a failure: it is this page
//     being older than the server. It says so, and says to reload.
//
// Nothing here reads a file key. A Figma file key is content and
// never leaves the server — the chip is addressed by `linkId`, a hash, which is
// also why "allow this file" sends an id and lets the server resolve the key.

import type { FigmaAllowRefusal, FigmaLinkState } from "@loom/shared";

import type { FigmaLinkSnapshot } from "../../chat/useWsSession";

/** Which of the five chip skins renders. Maps onto the `--ok/--warn/--bad`
 *  tokens already on `.loom-focus`; `work` is the in-flight amber. */
export type FigmaChipTone = "neutral" | "work" | "ok" | "warn" | "bad";

/**
 * The one act a chip may offer, or `null`.
 *
 * `connect` / `reconnect` are a full-page navigation to `/auth/figma/start` —
 * an OAuth round trip cannot be a `fetch`. `allow` is the typed
 * `figma_allow_file` frame. There is deliberately no `retry`: no retry frame
 * exists on the wire, and a button that does nothing is worse than a sentence
 * telling the user to ask again in the chat.
 */
export type FigmaChipAction = "connect" | "reconnect" | "allow" | null;

export interface FigmaChipCopy {
  readonly tone: FigmaChipTone;
  /** The state's own sentence. Always present — no state shares one. */
  readonly titleKey: string;
  /** The remedy, where there is one to give. */
  readonly bodyKey: string | null;
  readonly action: FigmaChipAction;
}

/**
 * Written as a `Record` over the closed union rather than a `switch` with a
 * `default`, so the compiler REQUIRES an entry per state. A `default` arm would
 * have accepted a new state silently, which is exactly the drift the
 * `_MissingEventType` guard in `lib/ws.ts` exists to prevent one level up.
 *
 * `unsupported_kind` is the one entry the table cannot settle alone — Slides
 * and a Community file need different remedies — so it carries the fallback and
 * `figmaChipCopy` refines it by `kind`.
 */
const FIGMA_STATE_COPY: Readonly<Record<FigmaLinkState, FigmaChipCopy>> = {
  recognized: {
    tone: "neutral",
    titleKey: "focus.figma.state.recognized",
    bodyKey: null,
    action: null,
  },
  // A link to a whole file. Nothing will be read until the user links a frame,
  // so the chip says how to get that link — the same thing the model is asking
  // for in the reply beside it. No button: the remedy happens in Figma.
  needs_frame: {
    tone: "warn",
    titleKey: "focus.figma.state.needsFrame",
    bodyKey: "focus.figma.state.needsFrameBody",
    action: null,
  },
  // The chip stays SHORT here on purpose: the connect card above it is
  // carrying the offer, the seat disclosure and both buttons, and printing the
  // same sentence twice forty pixels apart reads as a rendering fault.
  not_connected: {
    tone: "warn",
    titleKey: "focus.figma.state.notConnected",
    bodyKey: null,
    action: null,
  },
  declined: {
    tone: "neutral",
    titleKey: "focus.figma.state.declined",
    bodyKey: null,
    action: null,
  },
  unsupported_kind: {
    tone: "warn",
    titleKey: "focus.figma.state.unsupportedOther",
    bodyKey: null,
    action: null,
  },
  not_allowed: {
    tone: "warn",
    titleKey: "focus.figma.state.notAllowed",
    bodyKey: "focus.figma.state.notAllowedBody",
    action: "allow",
  },
  loading: {
    tone: "work",
    titleKey: "focus.figma.state.loading",
    bodyKey: null,
    action: null,
  },
  read: {
    tone: "ok",
    titleKey: "focus.figma.state.read",
    bodyKey: null,
    action: null,
  },
  // Figma answers 404 — not 403 — for a file an authenticated caller cannot
  // see (an acknowledged, unfixed bug). So this cannot name ONE cause, and
  // "ask your admin for access" would be wrong about half the time. Both
  // causes, and a remedy that tells the two apart from the user's side.
  unreachable: {
    tone: "bad",
    titleKey: "focus.figma.state.unreachable",
    bodyKey: "focus.figma.state.unreachableRemedy",
    action: null,
  },
  // Figma opened the FILE, and the frame the link names held nothing. Kept
  // apart from `unreachable`, whose remedy (reconnect with another account) is
  // wrong for a file that opens fine. No button: the fix is a fresh link.
  frame_missing: {
    tone: "warn",
    titleKey: "focus.figma.state.frameMissing",
    bodyKey: "focus.figma.state.frameMissingBody",
    action: null,
  },
  rate_limited: {
    tone: "work",
    titleKey: "focus.figma.state.rateLimitedSoon",
    bodyKey: null,
    action: null,
  },
  // A LONG block: a `low` seat's monthly budget, or a reset Figma put more than
  // an hour away. Never auto-retried, so no button. This is the title when
  // Figma named no reset; with one, `limitCopy.ts` names it as a date. The copy
  // claims no cap size and no month boundary — neither is known from a 429.
  seat_limited: {
    tone: "warn",
    titleKey: "focus.figma.limit.blocked",
    bodyKey: "focus.figma.limit.blockedBody",
    action: null,
  },
  token_expired: {
    tone: "warn",
    titleKey: "focus.figma.state.tokenExpired",
    bodyKey: "focus.figma.state.tokenExpiredBody",
    action: "reconnect",
  },
  degraded: {
    tone: "warn",
    titleKey: "focus.figma.state.degraded",
    bodyKey: "focus.figma.state.degradedBody",
    action: null,
  },
  // Nothing was read, and the remedy is the user's own: a smaller frame. No
  // button — there is no retry frame, and the same link would be too big again.
  too_large: {
    tone: "warn",
    titleKey: "focus.figma.state.tooLarge",
    bodyKey: "focus.figma.state.tooLargeBody",
    action: null,
  },
  // A Figma fault, not the link and not the account. The sentence carries its
  // own remedy (ask again), so there is no body to add.
  upstream_error: {
    tone: "warn",
    titleKey: "focus.figma.state.upstreamError",
    bodyKey: null,
    action: null,
  },
  // The project's `figma:` block does not parse. The user cannot fix that from
  // here, and an allow button would write into a block nobody can read — so the
  // chip names who can fix it, and offers nothing.
  config_malformed: {
    tone: "bad",
    titleKey: "focus.figma.state.configMalformed",
    bodyKey: "focus.figma.state.configMalformedBody",
    action: null,
  },
};

/**
 * How long a chip keeps "Allow this file" disabled after the server refused it,
 * for a refusal a second try can fix. The server refuses a new attempt for 30 s
 * from the END of the last one (`FIGMA_ALLOW_RETRY_COOLDOWN_MS`,
 * `apps/server/src/figma/link-registry.ts`); counting the same span from when
 * the refusal reached this page is always later than that, so the click this
 * re-offers is never one the server throttles.
 */
export const FIGMA_ALLOW_RETRY_MS = 30_000;

/**
 * A refused "Allow this file", by reason. A `Record` for the reason the state
 * table is one: a new refusal word must be a type error, not a silent click.
 *
 * `retry` says whether trying again could work. `no_allowlist` cannot (there is
 * no list to add to), `access_denied` cannot (the user's rights will not
 * change in thirty seconds), `unavailable` cannot (this server cannot write
 * project settings at all) and `unknown_link` cannot (the server no longer
 * knows the file; the same click would miss again) — those chips offer no
 * button at all. The other three are a write that did not happen; the button
 * comes back, disabled until {@link FIGMA_ALLOW_RETRY_MS} has passed. A reload
 * after the server's cooldown re-checks the project and may offer it again
 * (`reconcileFigmaAllowlistStates`).
 */
const ALLOW_REFUSAL_COPY: Readonly<
  Record<
    FigmaAllowRefusal,
    { readonly titleKey: string; readonly bodyKey: string; readonly retry: boolean }
  >
> = {
  no_allowlist: {
    titleKey: "focus.figma.allow.refused.noAllowlist",
    bodyKey: "focus.figma.allow.refused.noAllowlistBody",
    retry: false,
  },
  access_denied: {
    titleKey: "focus.figma.allow.refused.accessDenied",
    bodyKey: "focus.figma.allow.refused.accessDeniedBody",
    retry: false,
  },
  write_failed: {
    titleKey: "focus.figma.allow.refused.writeFailed",
    bodyKey: "focus.figma.allow.retryBody",
    retry: true,
  },
  concurrent_write: {
    titleKey: "focus.figma.allow.refused.concurrentWrite",
    bodyKey: "focus.figma.allow.retryBody",
    retry: true,
  },
  precondition_unavailable: {
    titleKey: "focus.figma.allow.refused.preconditionUnavailable",
    bodyKey: "focus.figma.allow.retryBody",
    retry: true,
  },
  unavailable: {
    titleKey: "focus.figma.allow.refused.unavailable",
    bodyKey: "focus.figma.allow.refused.unavailableBody",
    retry: false,
  },
  unknown_link: {
    titleKey: "focus.figma.allow.refused.unknownLink",
    bodyKey: "focus.figma.allow.refused.unknownLinkBody",
    retry: false,
  },
};

const UNKNOWN_COPY: FigmaChipCopy = {
  tone: "neutral",
  titleKey: "focus.figma.state.unknown",
  bodyKey: "focus.figma.state.unknownBody",
  action: null,
};

/**
 * The one lookup the chip renders from.
 *
 * `rate_limited` splits on whether the server sent a `Retry-After`: a countdown
 * with no number is a broken countdown. `retryAfterS` is deliberately NOT
 * sticky in the fold, so its absence here means "this attempt gave no number",
 * never "an older attempt's number expired".
 */
export const figmaChipCopy = (link: FigmaLinkSnapshot): FigmaChipCopy => {
  if (link.state === "unknown") return UNKNOWN_COPY;
  const base = FIGMA_STATE_COPY[link.state];
  if (link.state === "unsupported_kind") {
    if (link.kind === "slides" || link.kind === "deck") {
      return { ...base, titleKey: "focus.figma.state.unsupportedSlides" };
    }
    if (link.kind === "community") {
      return {
        ...base,
        titleKey: "focus.figma.state.unsupportedCommunity",
        bodyKey: "focus.figma.state.unsupportedCommunityBody",
      };
    }
    return base;
  }
  if (link.state === "rate_limited" && link.retryAfterS !== undefined) {
    return { ...base, titleKey: "focus.figma.state.rateLimited" };
  }
  // A refused allow shows the refusal INSTEAD of the offer: the same button
  // under the same sentence is exactly the "I clicked and nothing happened" the
  // refusal exists to end.
  if (link.state === "not_allowed" && link.allowRefusal !== undefined) {
    const refusal = ALLOW_REFUSAL_COPY[link.allowRefusal];
    return {
      ...base,
      titleKey: refusal.titleKey,
      bodyKey: refusal.bodyKey,
      action: refusal.retry ? "allow" : null,
    };
  }
  // The allow worked. `recognized` alone would say "Loom spotted a link", which
  // is true and useless: the user just changed what Loom may do, and the next
  // thing to know is that they can ask for the design now.
  if (link.state === "recognized" && link.allowed === true) {
    return {
      ...base,
      tone: "ok",
      titleKey: "focus.figma.allow.allowed",
      bodyKey: null,
    };
  }
  // The design was read and a later re-read was refused. The chip still says it
  // was read; the refusal line (`figmaRefreshCopy`) may bring the one act its
  // remedy needs — a link to connect again, never a frame.
  if (link.state === "read") {
    const refresh = figmaRefreshCopy(link);
    if (refresh !== null) return { ...base, action: refresh.action };
  }
  return base;
};

/**
 * The quiet line under a `read` chip whose latest re-read was refused.
 *
 * `key` is the sentence; `untilKey`, where there is one, is the same sentence
 * with a `{when}` for the instant Figma named. `action` is the remedy's act, and
 * only ever an href: `not_allowed` offers no Allow here, because the server's
 * allow precondition is a chip that SAYS `not_allowed`, and this one says `read`.
 *
 * A `Record` over the closed union for the reason the state table is one. The
 * states that are never a refusal share the generic line — a row naming one is a
 * server this page does not understand, and the line stays true.
 */
export interface FigmaRefreshCopy {
  readonly key: string;
  readonly untilKey: string | null;
  /**
   * How `untilKey`'s `{when}` reads. A short limit is relative ("in 4 days");
   * a long block is the reset Figma named as a date and time, the way the chip
   * of a never-read link says it (`limitCopy.ts`). Absent means relative.
   */
  readonly untilAs?: "instant";
  /**
   * The line once the instant the refusal named has passed. Without it the line
   * fell back to `key`, which says the limit still stands, and kept saying so
   * for as long as the chip was on screen. Only for a refusal that names an
   * instant: the others say nothing about time.
   */
  readonly overKey?: string;
  readonly action: FigmaChipAction;
}

const REFRESH_OTHER: FigmaRefreshCopy = {
  key: "focus.figma.state.refresh.other",
  untilKey: null,
  action: null,
};

const REFRESH_REFUSAL_COPY: Readonly<Record<FigmaLinkState, FigmaRefreshCopy>> = {
  rate_limited: {
    key: "focus.figma.state.refresh.rateLimited",
    untilKey: "focus.figma.state.refresh.rateLimitedUntil",
    overKey: "focus.figma.state.refresh.limitOver",
    action: null,
  },
  seat_limited: {
    key: "focus.figma.state.refresh.seatLimited",
    untilKey: "focus.figma.state.refresh.seatLimitedUntil",
    untilAs: "instant",
    overKey: "focus.figma.state.refresh.limitOver",
    action: null,
  },
  token_expired: {
    key: "focus.figma.state.refresh.tokenExpired",
    untilKey: null,
    action: "reconnect",
  },
  not_connected: {
    key: "focus.figma.state.refresh.notConnected",
    untilKey: null,
    action: "connect",
  },
  not_allowed: {
    key: "focus.figma.state.refresh.notAllowed",
    untilKey: null,
    action: null,
  },
  config_malformed: {
    key: "focus.figma.state.refresh.configMalformed",
    untilKey: null,
    action: null,
  },
  unreachable: {
    key: "focus.figma.state.refresh.unreachable",
    untilKey: null,
    action: null,
  },
  frame_missing: {
    key: "focus.figma.state.refresh.frameMissing",
    untilKey: null,
    action: null,
  },
  too_large: {
    key: "focus.figma.state.refresh.tooLarge",
    untilKey: null,
    action: null,
  },
  upstream_error: {
    key: "focus.figma.state.refresh.upstreamError",
    untilKey: null,
    action: null,
  },
  degraded: {
    key: "focus.figma.state.refresh.degraded",
    untilKey: null,
    action: null,
  },
  recognized: REFRESH_OTHER,
  needs_frame: REFRESH_OTHER,
  declined: REFRESH_OTHER,
  unsupported_kind: REFRESH_OTHER,
  loading: REFRESH_OTHER,
  read: REFRESH_OTHER,
};

export const figmaRefreshCopy = (
  link: FigmaLinkSnapshot,
): FigmaRefreshCopy | null => {
  if (link.state !== "read" || link.refreshRefusal === undefined) return null;
  const refused = link.refreshRefusal.state;
  return refused === "unknown" ? REFRESH_OTHER : REFRESH_REFUSAL_COPY[refused];
};

/**
 * What a chip is called.
 *
 * Several chips for ONE file all used to read "Figma file", and nothing on
 * screen told them apart. Only what may already be on the chip is used:
 *
 *  - after a read, the file's name and the FRAME's name — the user's own screen;
 *  - before one, the link's place among the Figma links of its message, when
 *    that message held more than one;
 *  - otherwise the kind of thing the chip is about: a frame for
 *    `frame_missing` (Figma opened the file; the frame is what is missing), a
 *    file for everything else.
 */
export type FigmaChipTitle =
  | { readonly text: string }
  | {
      readonly key: string;
      readonly index?: number;
      readonly count?: number;
    };

export const figmaChipTitle = (link: FigmaLinkSnapshot): FigmaChipTitle => {
  const file = link.label?.trim() ?? "";
  const frame = link.frameName?.trim() ?? "";
  if (file !== "" && frame !== "" && frame !== file) {
    return { text: `${file} · ${frame}` };
  }
  if (file !== "") return { text: file };
  if (frame !== "") return { text: frame };
  const place = link.inMessage;
  if (place !== undefined && place.count > 1) {
    return {
      key: "focus.figma.state.linkInMessage",
      index: place.index,
      count: place.count,
    };
  }
  if (link.state === "frame_missing") {
    return { key: "focus.figma.state.frameFallback" };
  }
  return { key: "focus.figma.fileFallback" };
};

/**
 * Which link the connect card is about, and whether this is the SECOND ask.
 *
 * `null` means no card: either nothing is waiting on a credential, or the user
 * has said "don't ask again" for the whole session. That latch is derived from
 * the replayed rows (`figmaSessionDeclined`), never latched in component state
 * — client replay is full, not seq-resume, so a React-held decline would
 * resurface as a fresh prompt on every reconnect, on a chip the user already
 * dismissed.
 *
 * `offerSession` follows the same "there is no third ask" rule: the first
 * decline is per-link and silent, and only once one exists does the card also
 * offer the session-wide out.
 */
export interface FigmaConnectPrompt {
  readonly linkId: string;
  readonly offerSession: boolean;
}

export const figmaConnectPrompt = (
  links: ReadonlyArray<FigmaLinkSnapshot>,
): FigmaConnectPrompt | null => {
  // `declineScope` rather than `state`: a declined chip a read later moved on to
  // `read` still carries what the user said. The same predicate as
  // `figmaSessionDeclined`, written out rather than imported: this module is
  // type-only against `useWsSession`, which the FocusChat tests mock whole.
  if (links.some((l) => l.declineScope === "session")) return null;
  const waiting = links.find((l) => l.state === "not_connected");
  if (waiting === undefined) return null;
  return {
    linkId: waiting.linkId,
    offerSession: links.some(
      (l) => l.state === "declined" || l.declineScope !== undefined,
    ),
  };
};

/**
 * `/auth/figma/start`, optionally carrying where to come back to.
 *
 * A same-origin PATH, never a URL: the server re-validates it as one (it also
 * refuses `//host` and `/\host`, both of which a browser reads as an authority)
 * and strips any `figma` key before appending its own outcome marker. Encoding
 * here is what keeps a `?` or `&` in the caller's own query from splitting the
 * parameter.
 */
export const figmaConnectHref = (returnTo?: string): string => {
  const base = "/auth/figma/start";
  if (returnTo === undefined || returnTo === "") return base;
  return `${base}?returnTo=${encodeURIComponent(returnTo)}`;
};

/**
 * The path to hand `returnTo`, with any stale outcome marker removed so a
 * second connect attempt cannot land back on the first one's error line.
 *
 * `URLSearchParams`, not a regex — and no lookbehind anywhere in this file:
 * `vite.config.ts` pins `safari14`, where a lookbehind group throws at runtime
 * and passes every vitest run, so the assertion that catches one is a source
 * scan in the test — which is why the sequence itself appears nowhere here.
 */
export const figmaReturnToPath = (
  pathname: string,
  search: string,
): string => {
  const params = new URLSearchParams(search);
  params.delete(FIGMA_RESULT_PARAM);
  const rest = params.toString();
  return rest === "" ? pathname : `${pathname}?${rest}`;
};

/** The query key `/auth/figma/callback` redirects back with. */
export const FIGMA_RESULT_PARAM = "figma";

/**
 * The callback's whole outcome vocabulary. Four machine words, no
 * server-composed prose — so each one gets a localized line here and an unknown
 * fifth renders nothing rather than leaking a code at the user.
 */
export type FigmaConnectResult =
  | "connected"
  | "access_denied"
  | "oauth_error"
  | "exchange_failed";

const RESULT_COPY: Readonly<Record<FigmaConnectResult, string | null>> = {
  // Success needs no line: the chips change state, which is the better signal.
  connected: null,
  access_denied: "focus.figma.result.accessDenied",
  oauth_error: "focus.figma.result.oauthError",
  exchange_failed: "focus.figma.result.exchangeFailed",
};

/** The copy key for a `?figma=…` marker, or `null` for none / unrecognised. */
export const figmaResultCopyKey = (raw: string | null): string | null => {
  if (raw === null) return null;
  if (!Object.prototype.hasOwnProperty.call(RESULT_COPY, raw)) return null;
  return RESULT_COPY[raw as FigmaConnectResult];
};
